import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Spinner,
  Toast,
  Tabs
} from '@shopify/polaris';
import {
  ExportIcon
} from '@shopify/polaris-icons';
import axios from 'axios';
import { API_CONFIG } from '../config/api';
import EnhancedCallDashboard from '../components/EnhancedCallDashboard';
import { CustomModal } from '../components/CustomModal';

interface CallAiAnalysisProps {
  callLogId: string;
  onClose: () => void;
}

interface CallLog {
  id: number;
  duration: number;
  call_type: string;
  status: string;
  direction: string;
  vendor?: string;
  call_sid?: string;
  called_at: string;
  phone?: string;
  called_from_number?: string;
  recording_url?: string;
  has_ai_data?: boolean;
  duration_formatted?: string;
}

interface PromptResult {
  id: number;
  tenant_id: number;
  call_log_id: number;
  prompt_model_id: number;
  response_json: any;
  metadata: {
    ui_metadata: {
      title: string;
      layout: string;
      summary: string;
      components: Array<{
        path: string;
        type: string;
        chart?: string;
        label?: string;
        scale?: number[];
        thresholds?: {
          good: number;
          warn: number;
        };
        items?: string[];
        badge_map?: Record<string, string>;
      }>;
    };
  };
  error_message?: string;
  created_at: string;
  updated_at: string;
}

interface AiData {
  transcription_and_translation?: Array<{
    time: string;
    speaker_name: string;
    transcription: string;
    translation?: string;
    confidence?: number;
    sentiment?: 'positive' | 'negative' | 'neutral';
    keywords?: string[];
    action_items?: string[];
  }>;
  prompt_results?: PromptResult[];
  // Only guaranteed fields from backend
  transcript?: string;
  translation?: string;
}

const CallAiAnalysis: React.FC<CallAiAnalysisProps> = ({ callLogId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiData, setAiData] = useState<AiData | null>(null);
  const [callLog, setCallLog] = useState<CallLog | null>(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [showAllTranslations, setShowAllTranslations] = useState(false);
  const [expandedTranslations, setExpandedTranslations] = useState<Set<number>>(new Set());
  
  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    loadAiAnalysis();
  }, [callLogId]);

  const loadAiAnalysis = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/v1/call_logs/${callLogId}/ai_analysis`);
      
      if (response.data.success) {
        setCallLog(response.data.data.call_log);
        // The prompt_results are at the top level, not inside ai_data
        const aiDataWithPromptResults = {
          ...response.data.data.ai_data,
          prompt_results: response.data.data.prompt_results || []
        };
        setAiData(aiDataWithPromptResults);
      } else {
        setError('Failed to load AI analysis: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load AI analysis');
    } finally {
      setLoading(false);
    }
  };

  const runAiAnalysis = async () => {
    try {
      setAnalyzing(true);
      setError(null);
      
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/v1/call_logs/${callLogId}/ai_analyze`, {});
      
      if (response.data.success) {
        showToast('AI analysis completed successfully!');
        await loadAiAnalysis();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run AI analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastActive(true);
    setTimeout(() => setToastActive(false), 3000);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const minsPart = mins > 0 ? `${mins} min` : '';
    const secsPart = `${secs} sec`;
    return minsPart ? `${minsPart} ${secsPart}` : secsPart;
  };

  const toggleTranslation = (index: number) => {
    const newExpanded = new Set(expandedTranslations);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTranslations(newExpanded);
  };

  const formatTime = (timeString: string) => {
    // Convert time string to MM:SS format
    if (!timeString) return '';
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return timeString;
  };

  const renderTranscriptAndTranslation = () => {
    if (!aiData) return null;

    const transcriptItems = Array.isArray(aiData.transcription_and_translation)
      ? aiData.transcription_and_translation
      : [];

    if (transcriptItems.length > 0) {
      return (
      <BlockStack gap="400">
        {/* Header with global translation toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 0',
          borderBottom: '1px solid #e1e3e5'
        }}>
          <Text variant="headingMd" as="h3">Translate to English</Text>
          <Button
            variant="tertiary"
            size="slim"
            onClick={() => setShowAllTranslations(!showAllTranslations)}
          >
            {showAllTranslations ? 'Hide Translations' : 'View Translation'}
          </Button>
        </div>

        {/* Chat-like transcript messages */}
        <div style={{ paddingTop: '4px' }}>
          {transcriptItems.map((item, index) => {
              const isExpanded = expandedTranslations.has(index);
              const showTranslation = showAllTranslations || isExpanded;
              const hasTranslation = item.translation && item.translation.trim();
              
              // Determine speaker type and styling
              const isCustomer = item.speaker_name?.toLowerCase().includes('customer') || 
                                item.speaker_name?.toLowerCase().includes('caller') ||
                                index % 2 === 0; // Fallback: alternate speakers
              
              const messageBgColor = isCustomer ? '#e3f2fd' : '#e8f5e8'; // Light blue for customer, light green for agent
              const timestampColor = isCustomer ? '#1976d2' : '#388e3c'; // Blue for customer, green for agent
              const speakerName = item.speaker_name || (isCustomer ? 'Customer Name' : 'Agent');
              
              return (
                <div key={index} style={{ 
                  marginBottom: '8px',
                  backgroundColor: messageBgColor,
                  borderRadius: '6px',
                  padding: '8px 12px',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  {/* Single line layout: timestamp, speaker, content, translation button */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'nowrap'
                  }}>
                    {/* Timestamp */}
                    <span style={{ 
                      fontSize: '12px', 
                      color: timestampColor, 
                      fontWeight: '500',
                      minWidth: '32px',
                      flexShrink: 0
                    }}>
                      {formatTime(item.time || '')}
                    </span>
                    
                    {/* Speaker Name */}
                    <span style={{ 
                      fontSize: '12px', 
                      fontWeight: '600',
                      color: '#202223',
                      minWidth: '80px',
                      flexShrink: 0
                    }}>
                      {speakerName}
                    </span>
                    
                    {/* Message Content */}
                    <div style={{ 
                      flex: 1,
                      fontSize: '13px',
                      color: '#202223',
                      lineHeight: '1.3',
                      minWidth: 0
                    }}>
                      {item.transcription}
                    </div>
                    
                    {/* Translation toggle button */}
                    {hasTranslation && (
                      <div style={{ flexShrink: 0 }}>
                        <Button
                          variant="tertiary"
                          size="micro"
                          onClick={() => toggleTranslation(index)}
                        >
                          {isExpanded ? 'Hide Translation' : 'See Translation'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Translation content - show below when expanded */}
                  {hasTranslation && showTranslation && (
                    <div style={{ 
                      marginTop: '6px',
                      padding: '6px 8px',
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      borderRadius: '4px',
                      border: '1px solid rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#6d7175', 
                        fontWeight: '500',
                        marginBottom: '2px' 
                      }}>
                        Translation:
                      </div>
                      <div style={{ 
                        fontSize: '12px',
                        color: '#202223',
                        lineHeight: '1.3'
                      }}>
                        {item.translation}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </BlockStack>
      );
    }

    // Fallback to simple transcript/translation fields
    const hasTranscript = aiData.transcript || aiData.translation;
    if (!hasTranscript) {
      return (
        <Card padding="500">
          <BlockStack gap="400" align="center">
            <Text variant="headingLg" as="h2">No Transcript Available</Text>
            <Text variant="bodyMd" as="p" tone="subdued">
              Transcript and translation data is not available for this call.
            </Text>
          </BlockStack>
        </Card>
      );
    }

    // Simple fallback for basic transcript/translation
    return (
      <div>
        {/* Header with global translation toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 0',
          borderBottom: '1px solid #e1e3e5'
        }}>
          <Text variant="headingMd" as="h3">Translate to English</Text>
          <Button
            variant="tertiary"
            size="slim"
            onClick={() => setShowAllTranslations(!showAllTranslations)}
          >
            {showAllTranslations ? 'Hide Translations' : 'View Translation'}
          </Button>
        </div>

        <div style={{ paddingTop: '4px' }}>
          {aiData.transcript && (
            <div style={{
              backgroundColor: '#e3f2fd',
              borderRadius: '6px',
              padding: '8px 12px',
              marginBottom: '8px',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div style={{ 
                fontSize: '13px',
                color: '#202223',
                lineHeight: '1.3'
              }}>
                {aiData.transcript}
              </div>
            </div>
          )}
          
          {aiData.translation && showAllTranslations && (
            <div style={{
              backgroundColor: '#e8f5e8',
              borderRadius: '6px',
              padding: '8px 12px',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div style={{ 
                fontSize: '11px', 
                color: '#6d7175', 
                fontWeight: '500',
                marginBottom: '2px' 
              }}>
                Translation:
              </div>
              <div style={{ 
                fontSize: '12px',
                color: '#202223',
                lineHeight: '1.3'
              }}>
                {aiData.translation}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };






  if (loading) {
    return (
      <CustomModal
        open={true}
        onClose={onClose}
        title="AI Call Analysis"
        size="medium"
        primaryAction={{
          content: 'Close',
          onAction: onClose,
        }}
      >
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <Spinner size="large" />
          <Text variant="bodyMd" as="p">Loading comprehensive AI analysis...</Text>
        </div>
      </CustomModal>
    );
  }

  return (
    <CustomModal
      open={true}
      onClose={onClose}
      title="AI Call Analysis"
      size="medium"
      primaryAction={{
        content: 'Close',
        onAction: onClose,
      }}
      secondaryActions={[
        {
          content: aiData ? 'Re-analyze' : 'Run AI Analysis',
          onAction: runAiAnalysis,
          loading: analyzing,
          disabled: analyzing
        },
        {
          content: 'Export Report',
          icon: ExportIcon,
          onAction: () => showToast('Export feature coming soon!')
        }
      ]}
    >
      <BlockStack gap="600">
        {/* Call Information */}
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between">
              <BlockStack gap="200">
                <Text variant="bodyLg" as="p" tone="subdued">
                  {callLog?.phone || callLog?.call_sid || 'Unknown Phone'} • {formatDuration(callLog?.duration || 0)}
                </Text>
              </BlockStack>
              <BlockStack gap="200" align="end">
                <InlineStack gap="200">
                  {callLog?.status && (
                    <Badge tone={callLog.status === 'answered' ? 'success' : 'critical'}>
                      {callLog.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  )}
                  {callLog?.direction && (
                    <Badge tone="info">
                      {callLog.direction.replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  )}
                </InlineStack>
              </BlockStack>
            </InlineStack>

            {/* Call Recording (merged here) */}
            {callLog?.recording_url && (
              <BlockStack gap="200">
                <Text variant="headingMd" as="h3">Call Recording</Text>

                <audio
                  controls
                  style={{ width: '100%' }}
                  onTimeUpdate={(e) => {
                    const audio = e.target as HTMLAudioElement;
                    setCurrentTime(audio.currentTime);
                    setDuration(audio.duration);
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onLoadedMetadata={(e) => {
                    const audio = e.target as HTMLAudioElement;
                    setDuration(audio.duration);
                  }}
                >
                  <source src={`${API_CONFIG.BASE_URL}${callLog.recording_url}`} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </BlockStack>
            )}
          </BlockStack>
        </Card>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <Text as="p">{error}</Text>
          </Banner>
        )}

        {!aiData && !analyzing && (
          <Banner tone="info">
            <Text as="p">No AI analysis available. Click "Run AI Analysis" to get started.</Text>
          </Banner>
        )}

        {analyzing && (
          <Banner tone="info">
            <InlineStack align="center" gap="200">
              <Spinner size="small" />
              <Text as="span">Running comprehensive AI analysis... This may take a few minutes.</Text>
            </InlineStack>
          </Banner>
        )}

        {/* Main Content - Tabbed View */}
        {aiData && (
          <Tabs
            tabs={[
              { id: 'analysis', content: 'Analysis' },
              { id: 'transcript', content: 'Transcript & Translation' }
            ]}
            selected={selectedTab}
            onSelect={setSelectedTab}
            fitted
          >
            {selectedTab === 0 && (
              <BlockStack gap="400">
                {aiData.prompt_results && aiData.prompt_results.length > 0 ? (
                  <EnhancedCallDashboard results={aiData.prompt_results} />
                ) : (
                  <Card>
                    <BlockStack gap="400" align="center">
                      <Text variant="headingLg" as="h2">
                        No Analysis Available
                      </Text>
                      <Text variant="bodyMd" as="p" tone="subdued">
                        Click "Run AI Analysis" to generate comprehensive call insights.
                      </Text>
                    </BlockStack>
                  </Card>
                )}
              </BlockStack>
            )}
            
            {selectedTab === 1 && (
              <BlockStack gap="400">
                {renderTranscriptAndTranslation()}
              </BlockStack>
            )}
          </Tabs>
        )}

        {/* Fallback when no AI data */}
        {!aiData && (
          <Card>
            <BlockStack gap="400" align="center">
              <Text variant="headingLg" as="h2">
                No AI Analysis Available
              </Text>
              <Text variant="bodyMd" as="p" tone="subdued">
                Click "Run AI Analysis" to generate comprehensive call insights.
              </Text>
            </BlockStack>
          </Card>
        )}
        </BlockStack>

      {toastActive && (
        <Toast
          content={toastMessage}
          onDismiss={() => setToastActive(false)}
          duration={3000}
        />
      )}
    </CustomModal>
  );
};

export default CallAiAnalysis;